import { useEffect, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import api from "../services/api";

const PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

const BatchEnrollmentAmaphilIdHistory = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  const totalPages = useMemo(() => {
    return lastPage || Math.max(1, Math.ceil(total / perPage));
  }, [lastPage, total, perPage]);

  useEffect(() => {
    const fetchHistory = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setLoadError("Missing access token.");
          return;
        }

        const response = await api.get("/underwriting/batch-enrollment-amaphil-id/history", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            search: query,
            status: statusFilter || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            page,
            per_page: perPage,
          },
        });

        if (response.data?.status !== "success") {
          setLoadError("Failed to load batch enrollment history.");
          return;
        }

        const data = response.data?.data || {};
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setLastPage(data.last_page || 1);
      } catch (error) {
        setLoadError("Failed to load batch enrollment history.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, [query, statusFilter, dateFrom, dateTo, sortBy, sortDir, page, perPage]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getStatusClass = (value) => {
    const normalized = String(value || "").toLowerCase();
    if (normalized === "success") {
      return "bg-emerald-100 text-emerald-700";
    }
    if (normalized === "failed") {
      return "bg-rose-100 text-rose-700";
    }
    return "bg-slate-100 text-slate-600";
  };

  const renderPages = () => {
    const buttons = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    for (let p = start; p <= end; p += 1) {
      buttons.push(
        <button
          key={p}
          type="button"
          onClick={() => setPage(p)}
          className={`min-w-[40px] rounded-xl border px-3 py-2 text-sm font-semibold ${
            p === page
              ? "border-[var(--brand-orange)] bg-orange-50 text-[var(--brand-orange-dark)]"
              : "border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
        >
          {p}
        </button>
      );
    }
    return buttons;
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
          Batch Uploads
        </p>
        <h1 className="mt-3 text-3xl font-semibold text-slate-900">
          Batch Enrollment Amaphil ID History
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Review batch enrollment uploads and inspect failed records.
        </p>
      </div>

      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[220px]">
              <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </form>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>Display</span>
              <select
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {PER_PAGE_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
              className="min-w-[180px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Uploaded From</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(event) => {
                  setDateFrom(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Uploaded To</span>
              <input
                type="date"
                value={dateTo}
                onChange={(event) => {
                  setDateTo(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>
        </div>

        {loadError && (
          <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {loadError}
          </div>
        )}

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
          <div className="w-full overflow-x-auto">
            <table className="min-w-[1000px] w-full text-left text-sm text-slate-600">
              <thead className="bg-[linear-gradient(135deg,var(--brand-orange)_0%,var(--brand-orange-dark)_100%)] text-white">
                <tr>
                  {[
                    { key: "status", label: "Status" },
                    { key: "uploaded_by", label: "Uploaded By" },
                    { key: "original_filename", label: "File Name" },
                    { key: "purchase_id", label: "Purchase ID" },
                    { key: "referral_code", label: "Referral Code" },
                    { key: "amaphil_id", label: "Amaphil ID" },
                    { key: "remarks", label: "Remarks" },
                    { key: "created_at", label: "Uploaded At" },
                  ].map((column) => (
                    <th
                      key={column.key}
                      onClick={() => handleSort(column.key)}
                      className="cursor-pointer px-4 py-3 text-xs font-semibold"
                    >
                      <div className="flex items-center gap-2">
                        {column.label}
                        {sortBy === column.key && (
                          <span className="text-white">{sortDir === "asc" ? "▲" : "▼"}</span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-xs font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {isLoading ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={9}>
                      Loading batch history...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={9}>
                      No batch records found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={`${row.status}-${row.id}-${index}`} className="border-t border-slate-100">
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusClass(
                            row.status
                          )}`}
                        >
                          {row.status || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {row.uploader_username || row.uploaded_by || "-"}
                      </td>
                      <td className="px-4 py-3">{row.original_filename || "-"}</td>
                      <td className="px-4 py-3">{row.purchase_id || "-"}</td>
                      <td className="px-4 py-3">{row.referral_code || "-"}</td>
                      <td className="px-4 py-3">{row.amaphil_id || "-"}</td>
                      <td className="px-4 py-3">{row.remarks || "-"}</td>
                      <td className="px-4 py-3">{formatDateTime(row.created_at)}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveRow(row);
                            setIsViewOpen(true);
                          }}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <div>
            Showing {rows.length} of {total} entries
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              First
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Previous
            </button>
            {renderPages()}
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {isViewOpen && activeRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-3xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Batch Upload
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Upload Details
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Review the full details for this upload record.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              {[
                { label: "Batch ID", value: activeRow.batch_id },
                { label: "Status", value: activeRow.status },
                { label: "Uploaded By", value: activeRow.uploader_username || activeRow.uploaded_by },
                { label: "File Name", value: activeRow.original_filename },
                { label: "Purchase ID", value: activeRow.purchase_id },
                { label: "Referral Code", value: activeRow.referral_code },
                { label: "Amaphil ID", value: activeRow.amaphil_id },
                { label: "Remarks", value: activeRow.remarks },
                { label: "Uploaded At", value: formatDateTime(activeRow.created_at) },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
                >
                  <p className="text-xs uppercase tracking-wide text-slate-400">
                    {item.label}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-700">
                    {item.value || "-"}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchEnrollmentAmaphilIdHistory;
